import { createSlice } from '@reduxjs/toolkit';

/**
 * LF360-1970 — whether there is a session is now a question only the server can
 * answer.
 *
 * It used to be `localStorage.user_token`: present meant signed in, and the
 * router could decide synchronously on the first render. The cookie is
 * HttpOnly, so the page cannot see it, and the honest answer at start-up is "I
 * do not know yet" — which is a third state, and leaving it out is how a
 * signed-in administrator gets bounced to the login screen for the half second
 * before the answer arrives.
 */
export const SESSION_UNKNOWN = 'unknown';
export const SESSION_AUTHENTICATED = 'authenticated';
export const SESSION_ANONYMOUS = 'anonymous';

export const userSlice = createSlice({
  name: 'user',
  initialState: { sessionStatus: SESSION_UNKNOWN },
  reducers: {
    setUser: (state, action) => ({
      ...state,
      ...action.payload,
      fullName:
        action?.payload?.firstName && action?.payload?.lastName
          ? `${action?.payload?.firstName} ${action?.payload?.lastName}`
          : '',
      sessionStatus: SESSION_AUTHENTICATED,
    }),

    // Everything the session held, gone in one move — so that "signed out" is
    // one state and not a user object that happens to have no token beside it.
    clearUser: () => ({ sessionStatus: SESSION_ANONYMOUS }),
  },
});

export const { setUser, clearUser } = userSlice.actions;

export const selectSessionStatus = (state) =>
  state.user?.sessionStatus || SESSION_UNKNOWN;

export const selectUser = (state) => state.user;
export const selectCurrentProject = (state) => state.projects?.current || null;

export const selectCurrentProjectRole = (state) => {
  const user = state.user;
  const project = state.projects?.current;

  if (!user || !project) return null;

  const list = user.UserProjects || [];
  const link = list.find((up) => up.projectId === project.id);

  return link?.role || null; // 'projectAdmin' | 'projectManager' | 'user' | null
};

export const selectIsAdmin = (state) => {
  const role = state.user?.role;
  return role === 'admin' || role === 'systemAdmin';
};

export const selectIsSystemAdmin = (state) =>
  state.user?.role === 'systemAdmin';

export const selectIsProjectAdmin = (state) =>
  selectCurrentProjectRole(state) === 'projectAdmin';

export const selectIsProjectManager = (state) =>
  selectCurrentProjectRole(state) === 'projectManager';

export const selectIsUser = (state) => {
  const orgRole = state.user?.role;
  const projectRole = selectCurrentProjectRole(state);

  if (orgRole === 'admin' || orgRole === 'systemAdmin') return false;

  if (projectRole === 'projectAdmin' || projectRole === 'projectManager') {
    return false;
  }

  return true;
};

export const selectCanAccessAdministration = (state) =>
  selectIsAdmin(state) ||
  selectIsProjectAdmin(state) ||
  selectIsProjectManager(state);

export const selectCanManageProjectUsers = (state) =>
  selectIsAdmin(state) ||
  selectIsProjectAdmin(state) ||
  selectIsProjectManager(state);

export const selectCanManageProjectSettings = (state) =>
  selectIsAdmin(state) || selectIsProjectAdmin(state);

export const selectCanManageContent = (state) =>
  selectIsAdmin(state) || selectIsProjectAdmin(state);

export default userSlice.reducer;
